/* eslint-disable @typescript-eslint/no-explicit-any */

import { sanitize } from '@ben/common/dom';
import Button from '@ben/orion/button';
import type { IFile } from '@ben/orion/file-upload-lists';
import { RIcon } from '@ben/orion/remix-icon/remix-icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { ITheme } from '@ben/orion/utils/theme';
import { CustomComponentTypes } from '@ben/shared/constants';
import { useAgeRestrictionMessage } from '@ben/shared/hooks/use-age-restriction-message';
import type { IValidationError } from '@ben/shared/types/validation';
import type { TFunction } from 'i18next';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { RestrictionsByType } from '../../../types/columbia';
import type {
  IModularBenefitComponent,
  IModularBenefitEnrolmentWindow,
} from '../../../types/modular-benefit';
import ConditionalApprovalInfo from '../conditional-approval-info';
import SliderProgress from '../slider-progress/slider-progress';
import WindowInfo from '../window-info';
import MultiSelectOption from './multi-select-option';

export interface IMultiSelectOption {
  label: string;
  description?: string;
  key: string;
  subComponents?: IModularBenefitComponent[];
  type?: string;
  max?: number;
  disabled?: boolean;
  reason?: string;
}

export type TNestedValue =
  | string
  | boolean
  | number
  | ChangeEvent<HTMLInputElement>
  | string[]
  | number[]
  | IFile[];

export interface ISliderValue {
  id: string;
  percentage: number | string;
}

export type TVariants =
  | {
      hasSlider?: false;
      values: string[];
      sliderValues?: never;
      onChange: (value: string[]) => void;
      onChangeSlider?: never;
      disabledSliders?: never;
    }
  | {
      hasSlider: true;
      values?: never;
      sliderValues: ISliderValue[];
      onChange?: never;
      onChangeSlider: (props: ISliderValue[], id?: string) => void;
      disabledSliders?: boolean;
    };

export interface IOptionsBase {
  max?: number;
  min?: number;
  borderColor?: string;
  title?: string;
  showTitle?: boolean;
  options: IMultiSelectOption[];
  disabled?: boolean;
  noGap?: boolean;
  childValues?: Record<string, any>[];
  onChildValueChange?: (
    parentKey: string,
    key: string,
    value: TNestedValue
  ) => void;
  validationErrorCallback?: (validationError: IValidationError) => void;
  benefitId: string | number;
  restrictionsByType?: RestrictionsByType;
  configWindow?: IModularBenefitEnrolmentWindow;
  isConditionalApprovalTriggered?: boolean;
  isOptIn?: boolean;
  backendErrors?: Record<string, unknown>[];
}

export type IOptions = IOptionsBase & TVariants;

export const MultiSelectOptions = ({
  max,
  min,
  borderColor,
  title,
  showTitle,
  options,
  values,
  onChange,
  disabled = false,
  onChildValueChange,
  childValues,
  onChangeSlider,
  sliderValues,
  disabledSliders = false,
  noGap = false,
  validationErrorCallback,
  hasSlider = false,
  benefitId,
  restrictionsByType,
  configWindow,
  isConditionalApprovalTriggered,
  isOptIn,
  backendErrors,
}: IOptions) => {
  const { t } = useTranslation();

  const isRadio = max === 1 && min === 1;

  const maxMinInformation = useMemo(() => {
    return getMaxMinInformation(t, max, min);
  }, [max, min, t]);

  const maxByTypeInformation = useMemo(() => {
    return getMaxByTypeInformation(t, options);
  }, [t, options]);

  const { ageRestrictionMessage } = useAgeRestrictionMessage(
    restrictionsByType,
    options
  );

  const handleChange = useCallback(
    (key: string, checked: boolean) => {
      if (hasSlider) {
        const newSliderValues = getNewSliderValues(
          checked,
          key,
          sliderValues ?? []
        );
        onChangeSlider?.(newSliderValues);
        return;
      }
      let newValues: string[] = [];
      if (isRadio) {
        newValues = [key];
      } else {
        newValues =
          checked && typeof values === 'object'
            ? [...(values || []), key]
            : (values?.filter((value) => value !== key) ?? []);
      }
      onChange?.(newValues);
    },
    [hasSlider, isRadio, onChange, onChangeSlider, sliderValues, values]
  );

  useEffect(() => {
    if (!validationErrorCallback) return;
    if (max && values && values?.length > max) {
      validationErrorCallback({
        hasError: true,
        message: [t('benefits.maxOptionsError', { max })],
      });
      return;
    }
    if (min && values && values?.length < min) {
      validationErrorCallback({
        hasError: true,
        message: [t('benefits.minOptionsError', { min })],
      });
      return;
    }
    validationErrorCallback({ hasError: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, min, max, t]);

  const handleSliderChange = useCallback(
    (id: string, percentage: number) => {
      if (!sliderValues) return;
      const newSliderValues = sliderValues.map((sliderValue) => {
        if (sliderValue.id === id) {
          return { ...sliderValue, percentage };
        }
        return sliderValue;
      });
      onChangeSlider?.(newSliderValues, id);
    },
    [onChangeSlider, sliderValues]
  );

  const handleNestedComponentChange = useCallback(
    (
      parentKey: string,
      key: string,
      value: TNestedValue,
      type: CustomComponentTypes | string
    ) => {
      let newValue = value;

      newValue =
        type === CustomComponentTypes.MULTI_SELECT
          ? value
          : ((value as { target?: { value: TNestedValue } }).target?.value ??
            value);

      onChildValueChange?.(parentKey, key, newValue);
    },
    [onChildValueChange]
  );

  const totalPercentage = useMemo(() => {
    const total =
      sliderValues?.reduce(
        (accumulator, value) => accumulator + Number(value.percentage || 0),
        0
      ) ?? 0;
    return Number.parseFloat(total.toFixed(2));
  }, [sliderValues]);

  const handleSplitEqually = useCallback(() => {
    if (!sliderValues || sliderValues.length === 0) return;

    const numberSliders = sliderValues.length;
    const basePercentage = 100 / numberSliders;

    const newSliderValues = sliderValues.map((value) => ({
      ...value,
      percentage: Number.parseFloat(basePercentage.toFixed(2)),
    }));

    const newTotalPercentage = newSliderValues.reduce(
      (sum, value) => sum + Number(value.percentage || 0),
      0
    );

    // If the total percentage is not 100, we need to adjust the first slider value
    // to make the total percentage 100.
    const remainder = Number.parseFloat((100 - newTotalPercentage).toFixed(2));
    if (remainder !== 0 && newSliderValues.length > 0) {
      newSliderValues[0].percentage = Number.parseFloat(
        (newSliderValues[0].percentage + remainder).toFixed(2)
      );
    }

    onChangeSlider?.(newSliderValues);
  }, [sliderValues, onChangeSlider]);

  return (
    <OuterContainer>
      {showTitle && title && (
        <Title
          variant={TypographyVariant.bodyLg2}
          id={`multi-select-options-title-${title}`}
          forwardedAs={TypographyVariant.h2}
        >
          {title}
        </Title>
      )}
      <>
        <MaxMinInformation variant={TypographyVariant.bodyMd1}>
          {maxMinInformation}
        </MaxMinInformation>
        <MaxMinInformation variant={TypographyVariant.bodyMd1}>
          {maxByTypeInformation}
        </MaxMinInformation>
      </>
      <AgeRestrictionMessage
        variant={TypographyVariant.bodyMd1}
        dangerouslySetInnerHTML={{ __html: sanitize(ageRestrictionMessage) }}
      />
      {configWindow && !isOptIn && (
        <StyledWindowInfo>
          <WindowInfo
            startDate={configWindow.startTimestamp}
            endDate={configWindow.endTimestamp}
          />
        </StyledWindowInfo>
      )}
      {isConditionalApprovalTriggered && (
        <StyledWindowInfo>
          <ConditionalApprovalInfo />
        </StyledWindowInfo>
      )}
      {hasSlider && totalPercentage !== undefined && (
        <SliderProgress
          totalPercentage={totalPercentage}
          sliderValues={sliderValues ?? []}
          disabled={disabledSliders}
        />
      )}
      <OptionsContainer>
        {Boolean(options?.length) && (
          <Container
            aria-labelledby={`multi-select-options-title-${title}`}
            noGap={noGap}
          >
            {options?.map((option, index) => {
              const isLast = index === options.length - 1;
              const selected =
                hasSlider && sliderValues
                  ? sliderValues.some((value) => value.id === option.key)
                  : values?.includes(option.key);
              // We need to differentiate between 0 and undefined, as 0 would
              // indicate that config has specifically asked for 0 dependants of type
              // to be selected.
              const maxForType =
                typeof option.max === 'number' ? option.max : max;
              const valuesLength = Number(
                option.max
                  ? values
                      ?.map((value) =>
                        options.find((option) => option.key === value)
                      )
                      ?.filter((value) => value?.type === option.type)?.length
                  : values?.length
              );

              const selectedIndex = hasSlider
                ? (sliderValues?.findIndex(
                    (value) => value.id === option.key
                  ) ?? -1)
                : (values?.indexOf(option.key) ?? -1);

              const childError =
                selectedIndex !== -1 &&
                backendErrors &&
                Array.isArray(backendErrors)
                  ? backendErrors[selectedIndex]
                  : undefined;

              return (
                <OptionContainer key={option.key}>
                  <MultiSelectOption
                    benefitId={benefitId}
                    handleChange={handleChange}
                    handleNestedComponentChange={handleNestedComponentChange}
                    option={option}
                    hasSlider={hasSlider}
                    isRadio={isRadio}
                    borderColor={borderColor}
                    selected={selected}
                    childValues={childValues}
                    disabled={disabled || option?.disabled}
                    noGap={noGap}
                    childError={childError}
                    disabledSliders={
                      disabledSliders ||
                      Boolean(
                        !selected &&
                        !isRadio &&
                        typeof maxForType === 'number' &&
                        valuesLength >= maxForType
                      )
                    }
                    sliderProps={{
                      label: option.label,
                      disabled:
                        !sliderValues?.some(
                          (value) => value.id === option.key
                        ) ||
                        disabled ||
                        disabledSliders,
                      value: Number(
                        sliderValues?.find((value) => value.id === option.key)
                          ?.percentage ?? 0
                      ),
                      onChange: (percentage) =>
                        handleSliderChange(option.key, percentage),
                    }}
                  />
                  {noGap && !isLast && <Divider />}
                </OptionContainer>
              );
            })}
            {hasSlider &&
              options.length > 1 &&
              totalPercentage !== undefined && (
                <SplitEquallyContainer>
                  <Button
                    variant="subtle"
                    disabled={
                      disabledSliders ||
                      !sliderValues?.length ||
                      sliderValues.length < 2
                    }
                    leftIcon={() => <RIcon icon="scales-line" />}
                    onClick={handleSplitEqually}
                  >
                    {t('benefits.splitEqually')}
                  </Button>
                </SplitEquallyContainer>
              )}
          </Container>
        )}
      </OptionsContainer>
    </OuterContainer>
  );
};

export default MultiSelectOptions;

const OuterContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Container = styled('fieldset')`
  ${({ noGap, theme }: { noGap: boolean; theme: ITheme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: ${noGap ? theme.spacing(0) : theme.spacing(3)};
    border: ${noGap ? `1px solid ${theme.palette.neutral[60]}` : 'none'};
    background-color: ${noGap ? theme.palette.neutral[40] : 'transparent'};
  `}
`;

const Divider = styled('div')`
  ${({ theme }) => css`
    background-color: ${theme.palette.neutral[40]};
    border-top: 1px solid ${theme.palette.neutral[60]};
    margin: ${theme.spacing(0, 2)};
  `}
`;

const Title = styled(Typography)`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(1.5)};
  `}
`;

const MaxMinInformation = styled(Typography)`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-bottom: ${theme.spacing('20px')};
    line-height: 0.5rem;
  `}
`;

const AgeRestrictionMessage = styled(Typography)`
  ${({ theme }) => css`
    & > ul {
      padding-left: ${theme.spacing(2)};
      margin-top: 0;
    }
  `}
`;

const OptionsContainer = styled('div')`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: ${theme.spacing(3)};
  `}
`;

const OptionContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const StyledWindowInfo = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(2)};
  `}
`;

const getNewSliderValues = (
  checked: boolean,
  key: string,
  sliderValues: ISliderValue[]
) => {
  if (checked) {
    return typeof sliderValues === 'object'
      ? [...sliderValues, { id: key, percentage: 0 }]
      : [{ id: key, percentage: 0 }];
  }
  return sliderValues?.filter((value) => value.id !== key);
};

const getMaxMinInformation = (t: TFunction, max?: number, min?: number) => {
  if (max && min && max === min) {
    return t(
      min === 1 ? 'benefits.minMaxEqualOption' : 'benefits.minMaxEqualOptions',
      { min }
    );
  }
  if (max && min) {
    return t(min === 1 ? 'benefits.minMaxOption' : 'benefits.minMaxOptions', {
      min,
      max,
    });
  }
  if (max) {
    return t(max === 1 ? 'benefits.maxOption' : 'benefits.maxOptions', {
      max,
    });
  }
  if (min) {
    return t(min === 1 ? 'benefits.minOption' : 'benefits.minOptions', {
      min,
    });
  }
  return '';
};

const getMaxByTypeInformation = (
  t: TFunction,
  options: IMultiSelectOption[]
) => {
  const typeLimits: string[] = [];

  const uniqueOptionsByType = options.filter(
    (option, index, self) =>
      index ===
      self.findIndex(
        (uniqueOptionByType) => uniqueOptionByType.type === option.type
      )
  );

  for (const option of uniqueOptionsByType) {
    if (typeof option.max === 'number' && option.type) {
      const label = t(`benefits.${option.type.toLowerCase()}`, {
        count: option.max,
      });
      typeLimits.push(label);
    }
  }

  if (typeLimits.length === 0) return '';

  const typeLimitText = typeLimits.join(', ');

  return t('benefits.selectUpTo', { limits: typeLimitText });
};

const SplitEquallyContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    padding-right: ${theme.spacing(3)};
    padding-bottom: ${theme.spacing(3)};
  `}
`;
